<template>
    <div>
        <template v-if="!isAdmin">
            <PermissionDenied />
        </template>
        <template v-else>
            <vs-tabs v-model="activeTab">
                <vs-tab label="List" icon-pack="feather" icon="icon-list">
                    <div class="mt-6">
                        <!-- Table Section Header -->
                        <vs-row class="mb-4">
                            <vs-col vs-w="6">
                                <h4 class="mt-2">Master Year List</h4>
                            </vs-col>
                            <vs-col vs-w="6" class="text-right flex justify-end">
                                <vs-button v-if="canCreate" color="primary" type="filled" @click="activeTab = 1; resetForm();" icon-pack="feather" icon="icon-plus" class="mr-2">Add New</vs-button>
                                <vx-tooltip text="Refresh Data" color="primary">
                                    <vs-button color="primary" type="filled" @click="loadData" :disabled="loading">
                                        <font-awesome-icon :icon="redoIcon" />
                                    </vs-button>
                                </vx-tooltip>
                            </vs-col>
                        </vs-row>

                        <!-- Search and Filter Row -->
                        <vs-row class="mb-3">
                            <vs-col vs-xs="12" vs-sm="9" vs-lg="9" class="px-0 pr-2">
                                <vs-input
                                    class="w-full"
                                    placeholder="Search by financial year..."
                                    v-model="filters.search"
                                />
                            </vs-col>
                            <vs-col vs-xs="12" vs-sm="3" vs-lg="3" class="px-0">
                                <InputSelect
                                    :model.sync="filters.filter_active"
                                    label=""
                                    placeholder="Filter by Active Year"
                                    container_class="w-full"
                                    :options="active_year_options"
                                />
                            </vs-col>
                        </vs-row>
                        
                        <vx-card>
                            <div v-if="loading" class="text-center p-4">
                                <h3>Loading Data.. Please wait..</h3>
                            </div>
                            
                            <vs-table :data="results" slot="no-body" :hoverFlat="true" stripe v-if="!loading" noDataText="">
                                <vs-tr>
                                    <vs-th style="min-width: 80px;">S.No</vs-th>
                                    <vs-th style="min-width: 180px;">Financial Year Start Date</vs-th>
                                    <vs-th style="min-width: 180px;">Financial Year End Date</vs-th>
                                    <vs-th style="min-width: 100px;">FY</vs-th>
                                    <vs-th style="min-width: 120px;">Active Year</vs-th>
                                    <vs-th style="min-width: 100px;" v-if="canEdit">Action</vs-th>
                                </vs-tr>
                                <template v-if="results.length > 0">
                                    <template v-for="(item, index) in results">
                                        <vs-tr :key="index">
                                            <vs-td>
                                                {{ filters.offset + index + 1 }}
                                            </vs-td>
                                            <vs-td>
                                                <FormatText :text="item.start_date || item.financial_year_start_date" format="date" />
                                            </vs-td>
                                            <vs-td>
                                                <FormatText :text="item.end_date || item.financial_year_end_date" format="date" />
                                            </vs-td>
                                            <vs-td>
                                                {{ item.fy_year || item.fy }}
                                            </vs-td>
                                            <vs-td>
                                                <span :class="(item.is_active !== undefined ? item.is_active : item.active_year) ? 'text-success' : 'text-danger'">
                                                    {{ (item.is_active !== undefined ? item.is_active : item.active_year) ? 'Yes' : 'No' }}
                                                </span>
                                            </vs-td>
                                            <vs-td v-if="canEdit">
                                                <vs-button @click="editItem(item)" color="primary" type="border" icon="icon-edit" icon-pack="feather" size="small"></vs-button>
                                            </vs-td>
                                        </vs-tr>
                                    </template>
                                </template>
                                <template v-else>
                                    <vs-tr>
                                        <vs-td :colspan="canEdit ? 6 : 5" class="text-center p-4">
                                            <p class="text-muted mb-0" v-if="has_active_filters">No results found for the current search/filter criteria.</p>
                                            <p class="text-muted mb-0" v-else>No data available</p>
                                        </vs-td>
                                    </vs-tr>
                                </template>
                            </vs-table>
                        </vx-card>
                        
                        <!-- Pagination -->
                        <template v-if="total_results">
                            <vs-row class="mt-3 text-center">
                                <vs-pagination :total="total_pages" v-model="current_page" goto class="mx-auto"></vs-pagination>
                            </vs-row>
                        </template>
                    </div>
                </vs-tab>

                <vs-tab v-if="canCreate || canEdit" :label="editingId ? 'Edit' : 'Add'" icon-pack="feather" :icon="editingId ? 'icon-edit' : 'icon-plus'">
                    <div class="mt-6">
                        <!-- Form Section -->
                        <vx-card noShadow noRound>
                            <div class="mb-4">
                                <h3 class="mb-2">{{ editingId ? 'Edit Master Year' : 'Create Master Year' }}</h3>
                                <p class="text-muted mb-4" v-if="!editingId">Fill in the details below to create a new master year entry.</p>
                                <p class="text-muted mb-4" v-else>Update the master year details below.</p>
                            </div>
                            
                            <vs-row>
                                <vs-col vs-xs="12" vs-sm="6" vs-lg="3" class="mb-4">
                                    <SelectDate 
                                        :selectedDate.sync="formData.financial_year_start_date" 
                                        label="Financial Year Start Date *"
                                        class="w-full"
                                        :allow_future_dates="true"
                                    />
                                    <small class="block text-danger mt-1" v-if="api_errors.start_date || api_errors.financial_year_start_date">
                                        {{ (api_errors.start_date || api_errors.financial_year_start_date || []).join(', ') }}
                                    </small>
                                </vs-col>
                                
                                <vs-col vs-xs="12" vs-sm="6" vs-lg="3" class="mb-4">
                                    <SelectDate 
                                        :selectedDate.sync="formData.financial_year_end_date" 
                                        label="Financial Year End Date *"
                                        class="w-full"
                                        :allow_future_dates="true"
                                    />
                                    <small class="block text-danger mt-1" v-if="api_errors.end_date || api_errors.financial_year_end_date">
                                        {{ (api_errors.end_date || api_errors.financial_year_end_date || []).join(', ') }}
                                    </small>
                                </vs-col>
                                
                                <vs-col vs-xs="12" vs-sm="6" vs-lg="3" class="mb-4">
                                    <vs-input
                                        v-model="formData.fy"
                                        label="FY *"
                                        placeholder="e.g., 22-23"
                                        class="w-full"
                                        @input="validateFy"
                                    />
                                    <small class="block text-warning mt-1" v-if="showFyWarning">
                                        Only numeric and special characters are allowed (e.g., 24-25).
                                    </small>
                                    <small class="block text-danger mt-1" v-if="api_errors.fy_year || api_errors.fy">
                                        {{ (api_errors.fy_year || api_errors.fy || []).join(', ') }}
                                    </small>
                                </vs-col>
                                
                                <vs-col vs-xs="12" vs-sm="6" vs-lg="3" class="mb-4">
                                    <label class="vs-input--label block mb-2">Active Year *</label>
                                    <div class="flex align-center">
                                        <vs-radio v-model="formData.active_year" vs-name="active_year" :vs-value="true" class="mr-4">Yes</vs-radio>
                                        <vs-radio v-model="formData.active_year" vs-name="active_year" :vs-value="false">No</vs-radio>
                                    </div>
                                    <small class="block text-danger mt-1" v-if="api_errors.is_active || api_errors.active_year">
                                        {{ (api_errors.is_active || api_errors.active_year || []).join(', ') }}
                                    </small>
                                </vs-col>
                            </vs-row>
                            
                            <vs-row class="mt-4">
                                <vs-col vs-xs="12" vs-sm="12" vs-lg="12" class="text-right">
                                    <vs-button @click="resetForm(); activeTab = 0;" color="warning" type="flat" class="mr-2" v-if="editingId">
                                        Cancel
                                    </vs-button>
                                    <vs-button @click="save" :disabled="submit_disabled" color="primary" type="filled">
                                        {{ editingId ? 'Update' : 'Save' }}
                                    </vs-button>
                                </vs-col>
                            </vs-row>
                        </vx-card>
                    </div>
                </vs-tab>
            </vs-tabs>
        </template>
    </div>
</template>

<script>
import { faRedo } from '@fortawesome/free-solid-svg-icons';
import { Request } from '../../utils/requests';

export default {
    data() {
        let offset = parseInt(this.$route.query.offset || 0);
        let limit = parseInt(this.$route.query.limit || 20);
        
        let current_page = offset == 0 ? 1 : Math.ceil(offset / limit) + 1;
        return {
            results: [],
            loading: false,
            submit_disabled: false,
            api_errors: {},
            editingId: null,
            total_results: null,
            current_page: current_page,
            filters: {
                search: this.$route.query.search || null,
                filter_active: this.$route.query.filter_active || null,
                offset: offset,
                limit: limit
            },
            active_year_options: [
                { value: null, label: 'All Years' },
                { value: 'true', label: 'Active' },
                { value: 'false', label: 'Inactive' },
            ],
            formData: {
                financial_year_start_date: null,
                financial_year_end_date: null,
                fy: null,
                active_year: false
            },
            activeTab: 0,
            showFyWarning: false
        }
    },
    computed: {
        redoIcon: () => faRedo,
        isAdmin() {
            return this.$store.state.auth.userRole == 'admin' || this.hasAccess('read', 'master_year', 'any');
        },
        canCreate() {
            return this.$store.state.auth.userRole == 'admin' || this.hasAccess('create', 'master_year', 'all');
        },
        canEdit() {
            return this.$store.state.auth.userRole == 'admin' || this.hasAccess('edit', 'master_year', 'all');
        },
        total_pages() {
            if(this.total_results == 0 || this.filters.limit == 0)  return 1
            return Math.ceil(this.total_results/this.filters.limit)
        },
        has_active_filters() {
            return !!(this.filters.search || this.filters.filter_active)
        }
    },
    watch: {
        filters: {
            deep: true,
            handler(newV) {
                if (this.current_page == null) return;

                if (this.timer) clearTimeout(this.timer);

                this.timer = setTimeout(() => {
                    this.$router.push({ 
                        name: this.$route.name, 
                        query: {
                            offset: newV.offset,
                            limit: newV.limit,
                            search: newV.search || undefined,
                            filter_active: newV.filter_active || undefined
                        } 
                    });
                }, 800);
            }
        },
        "$route.query"(newV) {
            this.loadData();
        },
        current_page(newV, oldV) {
            if (newV == oldV) return;
            this.filters.offset = (newV - 1) * this.filters.limit;
        }
    },
    mounted() {
        this.init()
    },
    methods: {
        loadData() {
            this.loading = true
            this.$vs.loading()
            
            let url = this.$store.state.apiendpoints.master_year
            
            let payload = {
                offset: this.filters.offset,
                limit: this.filters.limit
            }
            if (this.$store.state.organisation.organisation && this.$store.state.organisation.organisation.id) {
                payload.organisation = this.$store.state.organisation.organisation.id
            }
            if (this.filters.search) payload.search = this.filters.search
            if (this.filters.filter_active !== null && this.filters.filter_active !== undefined) payload.is_active = this.filters.filter_active
            
            Request(this, 'GET', url, payload).then(
                (res) => {
                    // Handle response structure: {status: true, data: {count: X, results: [...]}}
                    if (res && res.data && res.data.results) {
                        this.results = res.data.results
                        this.total_results = res.data.count || 0
                    } else if (res && res.data && Array.isArray(res.data)) {
                        this.results = res.data
                        this.total_results = res.data.length
                    } else if (Array.isArray(res)) {
                        this.results = res
                        this.total_results = res.length
                    } else if (res && res.results) {
                        this.results = res.results
                        this.total_results = res.count || res.results.length
                    } else {
                        this.results = []
                        this.total_results = 0
                    }
                    if (this.filters.search) {
                        const q = this.filters.search.toLowerCase()
                        this.results = this.results.filter(item =>
                            (item.fy_year && String(item.fy_year).toLowerCase().includes(q)) ||
                            (item.fy && String(item.fy).toLowerCase().includes(q))
                        )
                        this.total_results = this.results.length
                    }
                    if (this.filters.filter_active !== null && this.filters.filter_active !== undefined) {
                        const activeFilter = this.filters.filter_active === 'true'
                        this.results = this.results.filter(item => {
                            const isActive = item.is_active !== undefined ? Boolean(item.is_active) : Boolean(item.active_year)
                            return isActive === activeFilter
                        })
                        this.total_results = this.results.length
                    }
                    this.calculate_page()
                    this.loading = false
                    this.$vs.loading.close()
                },
                (err) => {
                    this.loading = false
                    this.$vs.loading.close()
                    this.$vs.notify({
                        time: 3000,
                        title: 'Error',
                        text: "An error occurred while loading data. Please try again!",
                        iconPack: 'feather',
                        icon: 'icon-alert-circle',
                        color: 'danger'
                    })
                }
            )
        },
        save() {
            if (!this.formData.financial_year_start_date || !this.formData.financial_year_end_date || !this.formData.fy) {
                this.$vs.notify({
                    time: 3000,
                    title: 'Error',
                    text: "Please fill all required fields!",
                    iconPack: 'feather',
                    icon: 'icon-alert-circle',
                    color: 'danger'
                })
                return
            }
            
            if (!this.$store.state.organisation.organisation || !this.$store.state.organisation.organisation.id) {
                this.$vs.notify({
                    time: 3000,
                    title: 'Error',
                    text: "Organisation not found!",
                    iconPack: 'feather',
                    icon: 'icon-alert-circle',
                    color: 'danger'
                })
                return
            }
            
            this.submit_disabled = true
            this.$vs.loading()
            
            let url = this.$store.state.apiendpoints.master_year
            let method = 'POST'
            
            if (this.editingId) {
                url += this.editingId + '/'
                method = 'PUT'
            }
            
            let payload = {
                organisation: this.$store.state.organisation.organisation.id,
                start_date: this.formData.financial_year_start_date,
                end_date: this.formData.financial_year_end_date,
                fy_year: this.formData.fy,
                is_active: this.formData.active_year
            }
            
            Request(this, method, url, null, payload).then(
                (res) => {
                    this.submit_disabled = false
                    this.$vs.loading.close()
                    this.$vs.notify({
                        time: 3000,
                        title: 'Success',
                        text: this.editingId ? "Updated Successfully!" : "Saved Successfully!",
                        iconPack: 'feather',
                        icon: 'icon-check-circle',
                        color: 'success'
                    })
                    this.resetForm()
                    this.loadData()
                    this.activeTab = 0
                },
                (err) => {
                    this.submit_disabled = false
                    this.$vs.loading.close()
                    if (err.error) {
                        this.api_errors = err.error
                        
                        // Extract specific error message if non_field_errors exists
                        let errorMsg = "Please fix the errors and try again!"
                        if (err.error.non_field_errors && Array.isArray(err.error.non_field_errors)) {
                            errorMsg = err.error.non_field_errors.join(', ')
                        } else if (typeof err.error === 'string') {
                            errorMsg = err.error
                        }

                        this.$vs.notify({
                            time: 5000,
                            title: 'Error',
                            text: errorMsg,
                            iconPack: 'feather',
                            icon: 'icon-alert-circle',
                            color: 'danger'
                        })
                    } else {
                        this.$vs.notify({
                            time: 3000,
                            title: 'Error',
                            text: "An unknown error occurred! Please try again!",
                            iconPack: 'feather',
                            icon: 'icon-alert-circle',
                            color: 'danger'
                        })
                    }
                }
            )
        },
        editItem(item) {
            this.editingId = item.id
            this.formData = {
                financial_year_start_date: item.start_date || item.financial_year_start_date,
                financial_year_end_date: item.end_date || item.financial_year_end_date,
                fy: item.fy_year || item.fy,
                active_year: item.is_active !== undefined ? item.is_active : (item.active_year || false)
            }
            this.activeTab = 1
            // Scroll to top
            if (this.$el) {
                this.$el.scrollIntoView({ behavior: 'smooth' });
            } else {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        },
        resetForm() {
            this.editingId = null
            this.formData = {
                financial_year_start_date: null,
                financial_year_end_date: null,
                fy: null,
                active_year: false
            }
            this.api_errors = {}
            
            // Scroll to top
            if (this.$el) {
                this.$el.scrollIntoView({ behavior: 'smooth' });
            } else {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        },
        calculate_page() {
            if (this.filters.offset == 0) {
                this.current_page = 1;
            } else {
                this.current_page = Math.ceil(this.filters.offset / this.filters.limit) + 1;
            }
        },
        init() {
            let offset = parseInt(this.$route.query.offset || 0);
            let limit = parseInt(this.$route.query.limit || 20);
            this.filters.offset = offset;
            this.filters.limit = limit;
            this.filters.search = this.$route.query.search || null;
            this.filters.filter_active = this.$route.query.filter_active || null;
            this.calculate_page();
            
            if (!this.$route.query.offset || !this.$route.query.limit) {
                this.$router.replace({ 
                    name: this.$route.name, 
                    query: { 
                        ...this.$route.query,
                        offset: this.filters.offset, 
                        limit: this.filters.limit 
                    } 
                });
            }
            
            this.loadData();
        },
        validateFy(val) {
            if (val) {
                if (/[a-zA-Z]/.test(val)) {
                    this.showFyWarning = true;
                    this.$nextTick(() => {
                        this.formData.fy = val.replace(/[a-zA-Z]/g, '');
                    });
                    
                    // Hide warning after 5 seconds
                    if (this.warningTimer) clearTimeout(this.warningTimer);
                    this.warningTimer = setTimeout(() => {
                        this.showFyWarning = false;
                    }, 5000);
                }
            } else {
                this.showFyWarning = false;
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.text-muted {
    color: #6c757d;
    font-size: 0.9rem;
}

.text-success {
    color: #28a745;
    font-weight: 500;
}

.text-danger {
    color: #dc3545;
    font-weight: 500;
}

.text-warning {
    color: #FF9F43;
    font-weight: 500;
}

.mb-4 {
    margin-bottom: 1.5rem;
}

.mb-2 {
    margin-bottom: 0.5rem;
}

.mt-2 {
    margin-top: 0.5rem;
}

.mr-4 {
    margin-right: 1rem;
}

.mt-4 {
    margin-top: 1.5rem;
}

.flex {
    display: flex;
}

.justify-between {
    justify-content: space-between;
}

.items-center {
    align-items: center;
}
</style>
